import { useEffect } from 'react';
import { useOnboardingProgress, OnboardingProgress } from '../../hooks/useOnboardingProgress';
import { useAuthStore } from '../../store/authStore';
import { getAdminAsSlug } from '../../hooks/useTenant';

type OnboardingKey = 'hasProfileImage' | 'hasVerifiedTrack' | 'hasPublishedVault' | 'hasAtLeastOneFan';

interface OnboardingGuardProps {
  slug: string;
  /** The onboarding condition key that must be true to access this page */
  requiredKey: OnboardingKey;
  children: React.ReactNode;
}

/**
 * Redirect guard for locked pages.
 * Checks the artist's onboarding progress and redirects to /dashboard
 * if the required condition has not been met.
 * 
 * Admin users impersonating an artist (via ?as= param) bypass the guard entirely.
 */
export default function OnboardingGuard({ slug, requiredKey, children }: OnboardingGuardProps) {
  const { user } = useAuthStore();
  const isAdminImpersonating = user?.role === 'admin' && !!getAdminAsSlug();

  // Skip all checks for admin impersonation
  if (isAdminImpersonating) {
    return <>{children}</>;
  }

  return <OnboardingGuardInner slug={slug} requiredKey={requiredKey}>{children}</OnboardingGuardInner>;
}

function OnboardingGuardInner({ slug, requiredKey, children }: OnboardingGuardProps) {
  const progress = useOnboardingProgress(slug);

  useEffect(() => {
    if (progress.loading) return;
    if (!(progress[requiredKey] as boolean)) {
      window.location.href = '/dashboard';
    }
  }, [progress.loading, progress[requiredKey]]);

  if (progress.loading) {
    return (
      <div className="min-h-screen bg-deep-charcoal flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-electric-cyan/30 border-t-electric-cyan rounded-full animate-spin" />
      </div>
    );
  }

  if (!(progress[requiredKey] as boolean)) {
    // Will redirect momentarily — show loading state
    return (
      <div className="min-h-screen bg-deep-charcoal flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-electric-cyan/30 border-t-electric-cyan rounded-full animate-spin" />
      </div>
    );
  }

  return <>{children}</>;
}
